import {XIcon} from '@heroicons/react/outline';
import clsx from 'clsx';
import React, {useEffect, useId} from 'react';
import {createPortal} from 'react-dom';

import Button from '@/components/common/Button';
import {doNothing} from '@/constants/formatters';

export interface ModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  width?: string;
  height?: string;
  zIndex?: number; // Update this on nested modals to show them above parent modals
  closeOnOutsideClick?: boolean;
  titleSize?: string;
  hideX?: boolean;
  dataCy?: string;
  allowOverflow?: boolean;
  /** Optional sticky footer rendered outside the scrollable body, pinned to the modal bottom. */
  footer?: React.ReactNode;
  /**
   * "center" (default) is the classic centered dialog. "right" renders a
   * full-height drawer pinned to the right edge of the viewport — used for
   * master-detail views where a compact side panel reads better than a
   * centered dialog (e.g. webhook delivery detail on mobile).
   */
  side?: 'center' | 'right';
}

// Number of open modals, so nested modals (and modals that close in the same
// commit) only unlock page scrolling once the last one has closed.
let openModalCount = 0;

const Modal: React.FC<React.PropsWithChildren<ModalProps>> = ({
  isOpen,
  onClose,
  width = 'w-1/3',
  height = 'h-auto',
  children,
  zIndex = 50,
  title,
  dataCy,
  closeOnOutsideClick = false,
  titleSize,
  hideX = false,
  allowOverflow = true,
  footer,
  side = 'center',
}) => {
  // Prevent scrolling when modal is open
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    openModalCount += 1;
    document.body.style.overflow = 'hidden';
    return () => {
      openModalCount -= 1;
      if (openModalCount === 0) {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isOpen]);

  // Render nothing if not open
  if (!isOpen) {
    return null;
  }

  // Create a portal div if it doesn't exist
  const modalRoot = document.getElementById('modal-root') ?? createModalRoot();
  const isRightDrawer = side === 'right';

  return createPortal(
    <div
      className={clsx(
        'fixed inset-0 flex items-center',
        isRightDrawer ? 'justify-end' : 'justify-center',
      )}
      style={{zIndex}}
      aria-modal="true"
      aria-labelledby={titleId}
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black opacity-50 cursor-pointer"
        onClick={closeOnOutsideClick ? onClose : doNothing}
      ></div>

      {/* Modal Content */}
      <div
        data-cy={dataCy ? `${dataCy}-container` : undefined}
        className={clsx(
          'bg-white shadow-lg relative flex flex-col',
          'dark:bg-dark-inputBg dark:text-white',
          isRightDrawer
            ? 'h-full w-11/12 sm:w-10/12 md:w-7/12 lg:w-1/3 overflow-y-auto'
            : clsx(
                'rounded-lg',
                width,
                height,
                allowOverflow
                  ? 'overflow-y-auto max-h-[500px]'
                  : 'overflow-hidden',
              ),
        )}
      >
        {/* Modal Header */}
        <div
          className={clsx(
            'flex items-center p-4 relative',
            isRightDrawer ? 'justify-between' : 'justify-center',
          )}
        >
          <h1
            id={titleId}
            data-cy={dataCy ? `${dataCy}-title` : undefined}
            className={`${titleSize || 'text-lg'} font-semibold ${isRightDrawer ? '' : 'text-center w-full'}`}
          >
            {title}
          </h1>

          {!hideX && (
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              aria-label="Close Modal"
              dataCy={dataCy ? `${dataCy}-close` : undefined}
              className={
                isRightDrawer ? 'no-padding' : 'no-padding absolute right-5'
              }
            >
              <XIcon className="w-6 h-6 text-gray-800 dark:text-white" />
            </Button>
          )}
        </div>
        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">{children}</div>

        {/* Sticky footer (pinned to the bottom, outside the scroll area) */}
        {footer && (
          <div className="shrink-0 px-4 py-3 border-t border-gray-100 dark:border-dark-borderMid">
            {footer}
          </div>
        )}
      </div>
    </div>,
    modalRoot,
  );
};

// Helper function to create modal root if it doesn't exist
function createModalRoot() {
  const modalRoot = document.createElement('div');
  modalRoot.setAttribute('id', 'modal-root');
  document.body.appendChild(modalRoot);
  return modalRoot;
}

export default Modal;
